import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Building2, Loader2, AlertTriangle, CheckCircle2, Lock } from 'lucide-react';

/**
 * CpscIorConfig — CHA-206. Importer-of-Record routing config. Shows each IOR, the divisions it covers, its
 * status (live / awaiting credentials), and lets an admin edit non-secret metadata + toggle enabled. Secret
 * VALUES are never shown or entered here — they live in Secret Manager under the ref names shown read-only.
 * Enabling is blocked server-side until metadata is complete AND both secret refs resolve.
 */
const META_FIELDS = [
  { key: 'certifierId', label: 'Certifier ID' },
  { key: 'collectionId', label: 'Collection ID' },
  { key: 'manufacturerId', label: 'Manufacturer ID' },
  { key: 'pocId', label: 'POC ID' },
];
const s = (v) => (v == null ? '' : String(v));

export default function CpscIorConfig({ userProfile } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';
  const arg = useCallback((extra = {}) => (isPlatformRole ? { tenantId, ...extra } : { ...extra }), [isPlatformRole, tenantId]);

  const [iors, setIors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [edits, setEdits] = useState({}); // iorId → { field: value }

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await httpsCallable(getFunctions(), 'listCpscIorConfig')(arg());
      setIors(res.data?.iors || []);
    } catch (e) { setError(e?.message || 'Could not load IOR configuration.'); } finally { setLoading(false); }
  }, [arg]);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  const saveMeta = useCallback(async (iorId) => {
    setBusy(iorId); setError(null); setNotice(null);
    try {
      await httpsCallable(getFunctions(), 'manageCpscIorConfig')(arg({ iorId, patch: edits[iorId] || {} }));
      setNotice(`${iorId} metadata saved.`); setEdits((e) => ({ ...e, [iorId]: {} })); await load();
    } catch (e) { setError(e?.message || 'Save failed.'); } finally { setBusy(null); }
  }, [arg, edits, load]);

  const toggle = useCallback(async (iorId, enabled) => {
    setBusy(iorId); setError(null); setNotice(null);
    try {
      await httpsCallable(getFunctions(), 'manageCpscIorConfig')(arg({ iorId, patch: edits[iorId] || {}, enabled }));
      setNotice(`${iorId} ${enabled ? 'enabled' : 'disabled'}.`); await load();
    } catch (e) { setError(e?.message || 'Could not change the enabled state.'); } finally { setBusy(null); }
  }, [arg, edits, load]);

  if (!isAdmin) return <div className="font-plex text-sm text-carbon-gray-70 bg-carbon-gray-10 border border-carbon-border px-4 py-6 text-center">IOR configuration is restricted to administrators.</div>;

  return (
    <div className="font-plex text-carbon-ink space-y-3 max-w-3xl">
      <div>
        <h3 className="text-base font-semibold text-carbon-ink flex items-center gap-1.5"><Building2 size={16} /> IOR Configuration</h3>
        <p className="text-sm text-carbon-gray-70">Each Importer of Record certifies its own divisions under its own Certifier ID + CPSC credentials. A CoC whose products ship in a disabled IOR's divisions is blocked until you load that IOR's credentials and enable it.</p>
      </div>
      {notice && <div className="text-xs text-carbon-blue-70 bg-carbon-blue-10 border border-carbon-blue-60 px-3 py-1.5">{notice}</div>}
      {error && <div className="text-sm text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-3 py-2 flex items-start gap-1"><AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{error}</span></div>}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-carbon-gray-70 px-1 py-6"><Loader2 size={15} className="animate-spin" /> Loading IORs…</div>
      ) : iors.map((ior) => {
        const ed = edits[ior.id] || {};
        const val = (k) => (ed[k] !== undefined ? ed[k] : s(ior[k]));
        return (
          <div key={ior.id} className={`bg-carbon-white border border-carbon-border border-l-[3px] p-3 space-y-2 ${ior.enabled ? 'border-l-carbon-green-50' : 'border-l-carbon-gray-30'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-plex-mono font-semibold text-carbon-ink">{ior.id}</span>
                {ior.enabled
                  ? <span className="font-plex-mono text-[10px] uppercase text-carbon-green-50 bg-carbon-green-10 border border-carbon-green-50 px-1.5 py-0.5 flex items-center gap-1"><CheckCircle2 size={11} /> live</span>
                  : <span className="font-plex-mono text-[10px] uppercase text-carbon-orange-70 bg-carbon-white border border-carbon-orange-40 px-1.5 py-0.5 flex items-center gap-1"><Lock size={11} /> awaiting credentials</span>}
                <span className="text-xs text-carbon-gray-70">divisions: {ior.divisions ? ior.divisions.join(', ') : 'all others (default)'}</span>
              </div>
              {ior.id !== 'ior_main' && (
                <button onClick={() => toggle(ior.id, !ior.enabled)} disabled={busy === ior.id} aria-pressed={ior.enabled} className={`text-xs px-2 py-1 border disabled:opacity-50 ${ior.enabled ? 'border-carbon-red-60 text-carbon-red-60 hover:bg-carbon-gray-10' : 'border-carbon-green-50 text-carbon-green-50 hover:bg-carbon-green-10'}`}>
                  {busy === ior.id ? '…' : ior.enabled ? 'Disable' : 'Enable'}
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {META_FIELDS.map((f) => (
                <label key={f.key} className="text-xs text-carbon-gray-70">
                  <span className="block mb-0.5 font-plex-mono text-[10px] uppercase tracking-[0.1em]">{f.label}</span>
                  <input value={val(f.key)} disabled={ior.id === 'ior_main'} onChange={(e) => setEdits((cur) => ({ ...cur, [ior.id]: { ...cur[ior.id], [f.key]: e.target.value } }))} className="w-full border border-carbon-gray-30 bg-carbon-gray-10 px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-carbon-blue-60 disabled:bg-carbon-gray-20" />
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className="font-plex-mono text-[10px] text-carbon-gray-50 flex items-center gap-1"><Lock size={10} /> secrets: <code>{ior.tokenSecretName}</code> / <code>{ior.secretSecretName}</code> (values never shown)</span>
              {ior.id !== 'ior_main' && <button onClick={() => saveMeta(ior.id)} disabled={busy === ior.id || !Object.keys(ed).length} className="text-xs px-2 py-1 bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-40">Save metadata</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
